import { useEffect, useMemo, useRef, useState } from "react";
import Card from "../../../components/Card/Card";
import List from "../../../components/List/List";
import Pagination from "../../../components/Pagination/Pagination";
import useGetDataPaginatedList from "../../../hooks/useGetDataPaginatedList";
import ClientLicenceService from "../../../services/ClientLicenceService";
import "./ListClientsLicense.css";
import { v4 as uuidv4 } from 'uuid';
import { LicensesBillingStatuses, pickRandomStatus, pickRandomType } from "../../../constants/licenses";
import { concatTime, formatDate } from "../../../utils/date";
import { BsThreeDots } from "react-icons/bs";

const ListClientsLicense = (props) => {
	const [data, setData] = useState([]);
	const [maxPage, setMaxPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(12);
	const [total, setTotal] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const [collumnClicked, setCollumnClicked] = useState({
		collumnTitle: "id",
		orderDirection: "DESC",
	});
	const cancelTokenRef = useRef(null);
	const [isLoading, setIsLoading] = useState(true);

	const getLicenses = async (page, filters, collumnClicked) => {
		if (cancelTokenRef.current) {
			cancelTokenRef.current.abort();
		}
		cancelTokenRef.current = new AbortController();
		const response = await ClientLicenceService.listLicencesFiltersPaginated(
			page,
			filters,
			collumnClicked,
			rowsPerPage,
			cancelTokenRef.current.signal
		);
		setIsLoading(false);
		setData(response?.data?.items);
		setMaxPage(Math.ceil(response?.data?.meta?.totalItems / rowsPerPage));
		setTotal(response?.data?.meta?.totalItems);
	};

	useEffect(() => {
		getLicenses(currentPage, props.filters, collumnClicked);
	}, [
		props.filters,
		collumnClicked,
		currentPage,
		rowsPerPage,
	]);

	const transformedData = useMemo(() => {
		if (!data) return [];
		return data.map((item) => ({
			...item,
			SN_equipment: item.SN_equipment || null,
		}));
	}, [data]);

	return (
		<>
			<div className="list-container list-visibility">
				<List
					handleCollumnTitleClick={(collumnIndex) =>
						setCollumnClicked(collumnIndex)
					}
					collumsObjIndex={[
						{
							collumnLabel: "Licença / Tag",
							index: "bindingCode",
							type: "custom",
							custom: ({ row }) => {
								return (
									<span className="inline-flex flex-col gap-2">
										<span>{row.bindingCode}</span>
										<span className="opacity-50 text-xs!">{uuidv4()}</span>
									</span>
								);
							}
						},
						{
							collumnLabel: "Status",
							type: "custom",
							custom: ({ row }) => {
								const status = pickRandomStatus();
								return (
									<span className="relative inline-flex flex-row items-center gap-2 font-semibold px-3! py-2!" style={{ '--statusColor': status.color }}>
										<span className="absolute z-10 size-full bg-(--statusColor)! opacity-10 inset-0 rounded-full"></span>
										<span className="relative z-20 block size-2 rounded-full bg-(--statusColor)"></span>
										<span className="relative z-20">{status.name}</span>
									</span>
								);
							}
						},
						{
							collumnLabel: "Cliente atual",
							type: "custom",
							custom: ({ row }) => {
								return "Distribuidora A";
							}
						},
						{
							collumnLabel: "Dispositivo",
							index: "SN_equipment",
							type: "custom",
							custom: ({ row }) => {
								return (
									<div className="flex flex-col gap-2">
										<div className="flex flex-row items-center gap-3">
											{(row.SN_equipment?.trim()?.length > 0) ? (
												<span>{row.SN_equipment}</span>
											) : (
												<span style={{ opacity: 0.36 }}>Sem equipamento</span>
											)}
										</div>
										<div className="opacity-40 text-xs!">
											{pickRandomType()}
										</div>
									</div>
								);
							}
						},
						{
							collumnLabel: "Vencimento",
							index: "end_date",
							type: "custom",
							custom: ({ row }) => {
								const dueDate = new Date(concatTime(row.end_date));
								const diffDays = Math.floor((dueDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
								let billingStatusKey = 'PAID';
								if (diffDays < 0) {
									billingStatusKey = 'OVERDUE';
								} else if (diffDays < 30) {
									billingStatusKey = 'EXPIRING';
								}
								const billingStatus = LicensesBillingStatuses[billingStatusKey];

								return (
									<span className="inline-flex flex-col gap-2" style={{ '--statusColor': billingStatus.color }}>
										<span>{formatDate(dueDate)}</span>
										<span className="text-(--statusColor)! font-semibold!">{diffDays} dias</span>
									</span>
								);
							}
						},
						{
							collumnLabel: "Financeiro",
							index: "end_date",
							type: "custom",
							custom: ({ row }) => {
								const dueDate = new Date(concatTime(row.end_date));
								const diffDays = Math.floor((dueDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
								let billingStatusKey = 'PAID';
								if (diffDays < 0) {
									billingStatusKey = 'OVERDUE';
								} else if (diffDays < 30) {
									billingStatusKey = 'EXPIRING';
								}
								const billingStatus = LicensesBillingStatuses[billingStatusKey];

								return (
									<span className="relative text-(--statusColor)! font-semibold px-3! py-2!" style={{ '--statusColor': billingStatus.color }}>
										<span className="absolute z-10 size-full bg-(--statusColor)! opacity-10 inset-0 rounded-full"></span>
										<span className="relative z-20">{billingStatus.name}{billingStatusKey === 'EXPIRING' ? ` em ${Math.abs(diffDays)} dias` : ''}</span>
									</span>
								);
							}
						},
						{
							collumnLabel: "Ações",
							index: 'actions',
							type: "custom",
							custom: ({ row }) => {
								return (
									<button type="button"><BsThreeDots size={18} /></button>
								);
							}
						}
					]}
					data={transformedData}
				/>
			</div>
			<div className="card-visibility">
				{data &&
					data.map((clientLicence, index) => (
						<Card
							data={clientLicence}
							key={index}
							delete={() =>
								ClientLicenceService.deleteClientLicence(clientLicence.id)
							}
							navigateOnDelete={"../consulta-licencas"}
							navigateOnEdit={"/cadastro-licencas"}
						>
							<p className="card-content-title">{clientLicence.bindingCode}</p>
							<p className="card-content-title">
								{clientLicence.SN_equipment
									? clientLicence.SN_equipment
									: "Sem equipamento"}
							</p>
							<p className="card-content">
								Data início: {clientLicence.start_date}
							</p>
							<p className="card-content">Data fim: {clientLicence.end_date}</p>
							<p className="card-content">
								Habilitada: {clientLicence.enabled ? "Sim" : "Não"}
							</p>
						</Card>
					))}
			</div>
			<Pagination
				currentPage={currentPage}
				rowsPerPage={rowsPerPage}
				maxPage={maxPage}
				total={total}
				changePage={(page) =>
					setCurrentPage(page)
				}
				changeRowsPerPage={(rowsPerPage) =>
					setRowsPerPage(rowsPerPage)
				}
			/>
		</>
	);
};

export default ListClientsLicense;
